import { useCallback, useEffect, useMemo, useState } from "react";
import type { AvailableColumn, SavedViewRead } from "@/api/types";

export interface UseColumnPreferencesOptions {
  /** Project slug — namespaces the localStorage key. */
  slug: string | null;
  /** Stable identifier for the list this hook drives, e.g. "companies",
   *  "contacts", "tasks". Combined with slug to form the storage key. */
  scope: string;
  /** Authoritative list of columns this list can render. The active list
   *  is always a subset (and reordering) of this. */
  available: AvailableColumn[];
  /** Currently-selected saved view, if any. The view's `columns` field
   *  takes priority over localStorage and defaults. */
  activeView: SavedViewRead | null;
  /** Optional override for the active list — when supplied, takes the
   *  place of the localStorage read (e.g. when the parent owns the
   *  storage at a unified key like `prism.{slug}.companies.viewState`). */
  initialActiveKeys?: string[] | null;
  /** When false, the hook stops persisting to its own localStorage key
   *  on setActiveKeys / reset. Use this when the parent owns persistence
   *  in a unified blob. Defaults to true for back-compat. */
  persistToLocalStorage?: boolean;
}

export interface UseColumnPreferencesResult {
  /** Ordered list of column keys to render. Locked + default columns
   *  always appear; user choices override the rest. */
  activeKeys: string[];
  /** True when the current selection differs from the active view's
   *  saved columns (or from the defaults if no view is selected). Used
   *  to surface the "modified" indicator on the view pill. */
  isDirty: boolean;
  /** Replace the active list. Persists to localStorage when no view is
   *  selected; the parent is responsible for forwarding to the view via
   *  saveToActiveView when a view *is* selected. */
  setActiveKeys: (next: string[]) => void;
  /** Reset to the active view's columns (if any) or to defaults. Also
   *  clears the localStorage entry. */
  reset: () => void;
  /** Reset to the backend defaults, ignoring the active view's saved
   *  columns. Used by the global "Clear filters" path, which must
   *  reach the pristine state even when the baseline is a customized
   *  "All" view. */
  resetToDefaults: () => void;
}

function storageKey(slug: string, scope: string): string {
  return `prism.${slug}.${scope}.columns`;
}

function defaultsFromAvailable(available: AvailableColumn[]): string[] {
  // Defaults preserve the backend's order so columns line up with what
  // the project_type author intended.
  return available.filter((c) => c.default || c.locked).map((c) => c.key);
}

/**
 * Sanitizes a list of column keys against the current `available` set:
 * drops keys the backend no longer ships and ensures every locked
 * column is present (locked columns can't be hidden).
 */
function sanitize(
  keys: string[],
  available: AvailableColumn[],
): string[] {
  const knownKeys = new Set(available.map((c) => c.key));
  const lockedKeys = available.filter((c) => c.locked).map((c) => c.key);
  const filtered = keys.filter((k) => knownKeys.has(k));
  // Make sure each locked key shows up exactly once, at its current
  // position if already present, or at the head if missing.
  const result: string[] = [];
  const seen = new Set<string>();
  for (const key of filtered) {
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(key);
  }
  for (const lockedKey of lockedKeys) {
    if (!seen.has(lockedKey)) {
      result.unshift(lockedKey);
      seen.add(lockedKey);
    }
  }
  return result;
}

function readFromStorage(slug: string, scope: string): string[] | null {
  try {
    const raw = localStorage.getItem(storageKey(slug, scope));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every((x) => typeof x === "string")) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

function writeToStorage(
  slug: string,
  scope: string,
  keys: string[] | null,
): void {
  try {
    if (keys === null) {
      localStorage.removeItem(storageKey(slug, scope));
    } else {
      localStorage.setItem(storageKey(slug, scope), JSON.stringify(keys));
    }
  } catch {
    // Storage quota / private mode — ignore; the in-memory state still
    // works for the rest of the session.
  }
}

/**
 * Resolves the active column list with the priority:
 *
 *   active saved view's `columns`  >  per-project localStorage  >  defaults
 *
 * Mutations from the column selector go through setActiveKeys; persistence
 * to a saved view (PATCH /views/{id}) is the parent's responsibility — the
 * hook only persists to localStorage when no view is selected.
 */
export function useColumnPreferences({
  slug,
  scope,
  available,
  activeView,
  initialActiveKeys,
  persistToLocalStorage = true,
}: UseColumnPreferencesOptions): UseColumnPreferencesResult {
  const defaults = useMemo(
    () => defaultsFromAvailable(available),
    [available],
  );

  // Baseline = the source the active list resets to. View if one is
  // selected, otherwise the defaults from the backend.
  const baseline = useMemo<string[]>(() => {
    if (activeView?.columns && activeView.columns.length > 0) {
      return sanitize(activeView.columns, available);
    }
    return defaults;
  }, [activeView, available, defaults]);

  const [activeKeys, setActiveKeysState] = useState<string[]>(() => {
    if (!slug) return defaults;
    if (activeView?.columns && activeView.columns.length > 0) {
      return sanitize(activeView.columns, available);
    }
    if (initialActiveKeys && initialActiveKeys.length > 0) {
      return sanitize(initialActiveKeys, available);
    }
    if (persistToLocalStorage) {
      const stored = readFromStorage(slug, scope);
      if (stored) return sanitize(stored, available);
    }
    return defaults;
  });

  // When the view changes (or available columns change), re-resolve.
  useEffect(() => {
    if (!slug) return;
    if (activeView?.columns && activeView.columns.length > 0) {
      setActiveKeysState(sanitize(activeView.columns, available));
      return;
    }
    if (initialActiveKeys && initialActiveKeys.length > 0) {
      setActiveKeysState(sanitize(initialActiveKeys, available));
      return;
    }
    if (persistToLocalStorage) {
      const stored = readFromStorage(slug, scope);
      setActiveKeysState(sanitize(stored ?? defaults, available));
      return;
    }
    setActiveKeysState(sanitize(defaults, available));
  }, [
    slug,
    scope,
    activeView,
    available,
    defaults,
    initialActiveKeys,
    persistToLocalStorage,
  ]);

  const setActiveKeys = useCallback(
    (next: string[]) => {
      const sanitized = sanitize(next, available);
      setActiveKeysState(sanitized);
      // Only persist to our own key when the consumer hasn't opted out
      // (the parent may own a unified storage blob) and no view is
      // selected (with a view, the parent decides whether to PATCH or
      // treat the change as a dirty-but-unsaved state).
      if (persistToLocalStorage && slug && !activeView) {
        writeToStorage(slug, scope, sanitized);
      }
    },
    [available, slug, scope, activeView, persistToLocalStorage],
  );

  const reset = useCallback(() => {
    setActiveKeysState(baseline);
    if (persistToLocalStorage && slug && !activeView) {
      writeToStorage(slug, scope, null);
    }
  }, [baseline, slug, scope, activeView, persistToLocalStorage]);

  // Like reset(), but always to the backend defaults — never the
  // active view's baseline. The two are identical when no view is
  // active; they diverge when a named view or a customized "All" is
  // the baseline, and the caller specifically wants pristine.
  const resetToDefaults = useCallback(() => {
    setActiveKeysState(defaults);
    if (persistToLocalStorage && slug && !activeView) {
      writeToStorage(slug, scope, null);
    }
  }, [defaults, slug, scope, activeView, persistToLocalStorage]);

  const isDirty = useMemo(() => {
    if (activeKeys.length !== baseline.length) return true;
    for (let i = 0; i < activeKeys.length; i += 1) {
      if (activeKeys[i] !== baseline[i]) return true;
    }
    return false;
  }, [activeKeys, baseline]);

  return { activeKeys, isDirty, setActiveKeys, reset, resetToDefaults };
}
